import React, { useRef } from "react";
import { NavigateButton } from "@/components/NavigateButton";
import { SeatingBadge } from "@/components/Seating";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { CalendarDays, Users, Copy, Download, WifiOff, ShieldCheck, Navigation, MapPin } from "lucide-react";
import { fmtMoney } from "@/components/EventCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui-kit";
import { BookingStatusPill, DeadlineNote } from "@/components/BookingStatus";
import { usePrefs } from "@/contexts/PrefsContext";
import { fmtTime, fmtDay } from "@/lib/time";
import { imgSrc } from "@/lib/img";
import { ShareButtons, ShareTiles } from "@/components/ShareButtons";
import { CalendarMenu } from "@/components/CalendarMenu";
import type { Booking } from "@/types";

const TILE = "flex-1 min-w-0 min-h-16 rounded-2xl bg-accent px-1 py-2 text-[11px] font-bold leading-tight inline-flex flex-col items-center justify-center gap-1 active:scale-95 transition-transform";

export const TicketDialog = ({ booking, online, onClose }: { booking: Booking | null; online: boolean; onClose: () => void }) => {
  const { t, lang } = usePrefs();
  const wrap = useRef<HTMLDivElement>(null);
  if (!booking) return null;
  const name = lang === "ar" ? booking.event_title_ar || booking.venue_name_ar : booking.event_title_en || booking.venue_name_en;

  const saveImage = () => {
    const canvas = wrap.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${booking.code}.png`;
    a.click();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent onOpenAutoFocus={(e: Event) => e.preventDefault()} className="rounded-3xl max-w-[calc(100vw-1.5rem)] sm:max-w-sm p-0 gap-0 overflow-hidden flex flex-col max-h-[calc(100dvh-1.5rem)]" data-testid="ticket-dialog">
        <DialogHeader className="bg-primary text-primary-foreground px-5 py-4 pe-12 text-start shrink-0">
          <DialogTitle className="font-display text-lg leading-snug line-clamp-2">{name}</DialogTitle>
          <p className="text-sm opacity-90 flex gap-3">
            <span className="inline-flex items-center gap-1"><CalendarDays className="h-4 w-4" />{fmtDay(booking.date, lang)}</span>
            <span className="inline-flex items-center gap-1"><Users className="h-4 w-4" />{booking.guests}</span>
          </p>
        </DialogHeader>
        <div className="p-4 space-y-3 text-center overflow-y-auto overscroll-contain" data-testid="ticket-body">
          <div ref={wrap} className="mx-auto w-fit rounded-3xl bg-white p-3 shadow-inner" data-testid="booking-qr-code">
            <QRCodeCanvas value={`WAINNROOH|${booking.booking_id}|${booking.code}`} size={176} level="M" />
          </div>
          <div>
            <p data-testid="ticket-code" className="font-mono text-xl font-black tracking-widest" dir="ltr">{booking.code}</p>
            <p className="text-sm font-bold text-muted-foreground" data-testid="ticket-name">{booking.customer_name}</p>
          </div>
          <div className={`flex items-center justify-center gap-1.5 text-[11px] font-semibold rounded-full py-1.5 ${online ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-amber-500/15 text-amber-600"}`} data-testid="ticket-offline-status">
            {online ? <ShieldCheck className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
            {t("bookings.savedOffline")}
          </div>
          <NavigateButton lat={booking.venue_lat} lng={booking.venue_lng} name={booking.venue_name_ar} fallbackUrl={booking.directions_url || booking.terms_snapshot?.maps_url} testId="ticket-directions"
            className="h-12 w-full rounded-full bg-foreground text-background font-bold text-sm inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
            <Navigation className="h-4 w-4" />{t("ev.directions")}
          </NavigateButton>
          <div className="flex gap-2" data-testid="ticket-actions">
            <ShareTiles b={booking} tile={TILE} />
            <button data-testid="ticket-copy-button" onClick={() => { navigator.clipboard?.writeText(booking.code); toast.success(t("bookings.copied")); }} className={TILE}>
              <Copy className="h-5 w-5" /><span>{t("bookings.copyShort")}</span>
            </button>
            <button data-testid="ticket-save-button" onClick={saveImage} className={TILE}>
              <Download className="h-5 w-5" /><span>{t("bookings.saveShort")}</span>
            </button>
            {booking.event_id && booking.start_at && booking.end_at && (
              <CalendarMenu tile eventId={booking.event_id} title={name} start={booking.start_at} end={booking.end_at} location={lang === "ar" ? booking.venue_name_ar : booking.venue_name_en} className={TILE} />
            )}
          </div>
          {booking.seating && <p className="flex justify-between text-sm"><span className="text-muted-foreground">{t("seat.type")}</span><b data-testid="ticket-seating">{t(`seat.${booking.seating}`)}</b></p>}
          {booking.price_snapshot?.total && (
            <p className="flex justify-between text-sm"><span className="text-muted-foreground">{t("bk.total")}</span><b dir="ltr" data-testid="ticket-total">{fmtMoney(booking.price_snapshot.total)}</b></p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Notice = ({ tone, children, testid }: { tone: "amber" | "rose" | "zinc"; children: React.ReactNode; testid: string }) => (
  <div data-testid={testid} className={`rounded-2xl px-3 py-2 text-xs font-semibold ${tone === "amber" ? "bg-amber-500/15 text-amber-700 dark:text-amber-400" : tone === "rose" ? "bg-rose-500/10 text-rose-600 dark:text-rose-400" : "bg-muted text-muted-foreground"}`}>{children}</div>
);

const outcome = (b: Booking, t: (k: string) => string) => {
  if (b.status === "cancelled") {
    if (b.cancel_reason === "event_cancelled") return t("b5.eventCancelled");
    if (b.late_cancellation) return t("b5.late");
    if (b.cancelled_by === "venue") return `${t("b5.byVenue")}${b.cancel_reason ? ` — ${b.cancel_reason}` : ""}`;
  }
  if (b.status === "rejected" && b.decision_note) return `${t("b5.reason")}: ${b.decision_note}`;
  return "";
};

export interface RowActions { onOpen: () => void; onCancel?: () => void; onChange?: () => void; onMaterial?: (accept: boolean) => void; onDispute?: () => void; extra?: React.ReactNode }

export const BookingRow = ({ b, onOpen, onCancel, onChange, onMaterial, onDispute, extra }: { b: Booking } & RowActions) => {
  const { t, lang } = usePrefs();
  const active = (b.status === "pending" || b.status === "confirmed") && !b.checked_in;
  const ready = b.status === "confirmed" && !b.checked_in;
  const cr = b.change_request;
  const mc = b.material_change?.status === "pending" ? b.material_change : null;
  const note = outcome(b, t);
  const changeable = active && !cr?.status?.startsWith("pend") && b.terms_snapshot?.booking_cutoff && new Date(b.terms_snapshot.booking_cutoff) > new Date();
  const chip = "h-9 px-3 rounded-full text-xs font-bold";
  return (
    <div data-testid={`booking-item-${b.booking_id}`} className="flex gap-3 rounded-3xl border bg-card p-3 hover:shadow-lg transition-[box-shadow] duration-300">
      <img src={imgSrc(b.venue_image)} alt="" className="h-24 w-24 rounded-2xl object-cover shrink-0" />
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <p className="font-bold leading-snug truncate">{lang === "ar" ? b.event_title_ar || b.venue_name_ar : b.event_title_en || b.venue_name_en}</p>
          <BookingStatusPill b={b} />
        </div>
        <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtDay(b.date, lang)}</span>
          <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{b.guests} {t("bookings.guests")}</span>
          <SeatingBadge s={b.seating} testId={`booking-seating-${b.booking_id}`} />
        </p>
        <DeadlineNote b={b} />
        {b.status === "confirmed" && !b.checked_in && b.start_at && Math.abs(new Date(b.start_at).getTime() - Date.now()) < 3 * 3600e3 && (
          <p data-testid={`booking-checkin-by-${b.booking_id}`} className="text-xs font-semibold text-amber-700 dark:text-amber-400">
            {t("b7.checkBy").replace("{t}", fmtTime(new Date(new Date(b.start_at).getTime() + 30 * 60000).toISOString(), lang))}
          </p>
        )}
        {!!b.attended_count && b.attended_count < b.guests && <p data-testid={`booking-attended-${b.booking_id}`} className="text-xs font-semibold">{t("b6.attendedOf").replace("{a}", String(b.attended_count)).replace("{g}", String(b.guests))}</p>}
        {b.status === "pending" && <p className="text-[11px] text-muted-foreground">{t("b5.noQrYet")}</p>}
        {note && <Notice tone="zinc" testid={`booking-outcome-${b.booking_id}`}>{note}</Notice>}
        {cr?.status === "pending" && <Notice tone="amber" testid={`booking-change-pending-${b.booking_id}`}>{t("b5.changePending").replace("{n}", String(cr.guests))}</Notice>}
        {cr?.status === "rejected" && active && <Notice tone="zinc" testid={`booking-change-rejected-${b.booking_id}`}>{t("b5.changeRejected").replace("{r}", cr.reason || "")}</Notice>}
        {mc && active && (
          <Notice tone="rose" testid={`booking-material-${b.booking_id}`}>
            <p>{t("b5.material").replace("{f}", mc.fields.map((f) => t(`mf.${f}`)).join("، "))}</p>
            {onMaterial && (
              <div className="flex gap-2 pt-2">
                <button data-testid={`booking-material-accept-${b.booking_id}`} onClick={() => onMaterial(true)} className="h-8 px-3 rounded-full bg-foreground text-background">{t("b5.accept")}</button>
                <button data-testid={`booking-material-decline-${b.booking_id}`} onClick={() => onMaterial(false)} className="h-8 px-3 rounded-full border border-current">{t("b5.declineCancel")}</button>
              </div>
            )}
          </Notice>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {extra}
          {ready && (
            <button data-testid={`booking-show-qr-${b.booking_id}`} onClick={onOpen} className="h-9 px-4 rounded-full bg-primary text-primary-foreground text-xs font-bold active:scale-95 transition-transform">
              {t("bookings.showQr")}
            </button>
          )}
          {ready && b.directions_url && (
            <NavigateButton lat={b.venue_lat} lng={b.venue_lng} name={b.venue_name_ar} fallbackUrl={b.directions_url} testId={`booking-location-${b.booking_id}`}
              className="h-9 px-4 rounded-full bg-foreground text-background text-xs font-bold inline-flex items-center gap-1.5 active:scale-95 transition-transform">
              <MapPin className="h-3.5 w-3.5" />{t("bookings.location")}
            </NavigateButton>
          )}
          {ready && <ShareButtons b={b} compact />}
          {changeable && onChange && <button data-testid={`booking-change-${b.booking_id}`} onClick={onChange} className={`${chip} bg-accent`}>{t("b5.change")}</button>}
          {["no_show", "attended", "completed"].includes(b.status) && onDispute && !b.no_show_voided && (
            <button data-testid={`booking-dispute-${b.booking_id}`} onClick={onDispute} className={`${chip} bg-accent`}>{t("b6.dispute")}</button>
          )}
          {active && onCancel && (
            <button data-testid={`booking-cancel-${b.booking_id}`} onClick={onCancel} className={`${chip} text-muted-foreground hover:text-primary`}>
              {t("bookings.cancel")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
